/**
 * Auth store — holds the Proximi.io Bearer token + identity for the session.
 *
 * The token is the single credential the whole data layer needs:
 *  - the REST client passes it as `Authorization: Bearer <token>` (see
 *    `lib/api/client.ts`);
 *  - the MapLibre map injects it via `transformRequest` on every
 *    `*.proximi.io` / `*.proximi.fi` asset/tile/glyph request (Phase 1b).
 *
 * It is persisted to `localStorage` so a refresh keeps the session (and so the
 * map can mount with creds without re-login). This is a deliberately small
 * React Context — no extra store dependency — matching `app-store.tsx`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { login } from "@/lib/api";
import type { LoginResponse, Organization, User } from "@/lib/api";

const STORAGE_KEY = "proximiio.auth";

export interface AuthState {
  token: string | null;
  organization: Organization | null;
  user: User | null;
}

export interface AuthStore extends AuthState {
  /** Authenticate against the BFF `POST /api/auth/login` and persist the session. */
  signIn: (email: string, password: string) => Promise<void>;
  /** Clear the persisted session. */
  signOut: () => void;
}

const emptyState: AuthState = { token: null, organization: null, user: null };

function readPersisted(): AuthState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState;
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    if (typeof parsed.token !== "string") return emptyState;
    return {
      token: parsed.token,
      organization: parsed.organization ?? null,
      user: parsed.user ?? null,
    };
  } catch {
    return emptyState;
  }
}

function persist(state: AuthState): void {
  try {
    if (state.token) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode) — session stays in-memory only */
  }
}

const AuthContext = createContext<AuthStore | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(readPersisted);

  const signIn = useCallback(async (email: string, password: string) => {
    const res: LoginResponse = await login({ email, password });
    const next: AuthState = {
      token: res.token,
      organization: res.organization,
      user: res.user,
    };
    persist(next);
    setState(next);
  }, []);

  const signOut = useCallback(() => {
    persist(emptyState);
    setState(emptyState);
  }, []);

  const store = useMemo<AuthStore>(
    () => ({ ...state, signIn, signOut }),
    [state, signIn, signOut],
  );

  return <AuthContext.Provider value={store}>{children}</AuthContext.Provider>;
}

 
export function useAuth(): AuthStore {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
